// UnitsTable.jsx — matches .inv-panel / .inv-header / .inv-toolbar / table from HTML
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography,
  Popover,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { fmtPrice } from '../../../../contexts/InventoryContext';
import { getEffectiveCommissionDisplay, UNIT_STATUS_FILTERS_FALLBACK, SETTABLE_UNIT_STATUSES } from './constants';
import { STATUS_DOT, UnitTableStyles, pickPrimaryArea } from '../../../../utils/inventory_helper';
import {
  SmartTableTH,
  SmartTableTD,
  TableLoadingState,
  TableEmptyState,
  TABLE_ROW_GLOBAL_CSS,
  TABLE_ROW_CLASS,
} from '../../../../components/common/SmartTable';
import { TABLE_THEME } from '../../../../utils/smart_table_helper';
// Default status config (label can be overridden from API)
const DEFAULT_STATUS_CFG = {
  available:     { ...STATUS_DOT.available, label: 'Available' },
  booked:        { ...STATUS_DOT.booked,    label: 'Booked' },
  sold:          { ...STATUS_DOT.sold,      label: 'Sold' },
  blocked:       { ...STATUS_DOT.blocked,   label: 'Blocked' },
  not_available: { ...STATUS_DOT.not_available,    label: 'Not Available' },
};

function normalizeStatus(s) {
  if (!s) return 'available';
  const v = String(s).toLowerCase();
  return ['available', 'booked', 'sold', 'blocked', 'not_available'].includes(v) ? v : 'available';
}

/**
 * Resolves the display area from the unit's `area` JSON field.
 *
 * Priority:
 *   1. carpet_area
 *   2. builtup_area
 *   3. super_built_up_area
 *   4. total_area
 *   5. first custom key found
 *
 * Each area entry is expected to be: { value: number, unit: string }
 * or a plain number (legacy).
 *
 * Returns: { label: string, value: number, unit: string } | null
 */
/** "carpet_area" -> "Carpet Area" */
function formatAreaLabel(key) {
  if (key == null) return '';
  const str = String(key).replace(/_/g, ' ');
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Normalize unit.area to list of { key, unit, value }.
 * Accepts: [{"key": "carpet_area", "unit": "sqft", "value": 450}] or object form.
 */
function normalizeAreaList(area) {
  if (!area) return [];
  if (Array.isArray(area)) {
    return area
      .filter((item) => item && (item.key || item.slug) && (item.value != null && !Number.isNaN(Number(item.value))))
      .map((item) => ({
        key: item.key || item.slug || 'area',
        unit: item.unit || 'sqft',
        value: Number(item.value),
      }));
  }
  if (typeof area === 'object') {
    return Object.entries(area)
      .filter(([, v]) => v != null && !Number.isNaN(Number(typeof v === 'object' ? v.value : v)))
      .map(([k, v]) => ({
        key: k,
        unit: (typeof v === 'object' ? v.unit : null) || 'sqft',
        value: Number(typeof v === 'object' ? v.value : v),
      }));
  }
  return [];
}

// .btn styles reused
const btnBase = {
  display: 'inline-flex', alignItems: 'center', gap: '6px',
  borderRadius: '8px', fontSize: '12.5px', fontWeight: 600,
  cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '-0.1px',
  transition: 'all 150ms ease', border: 'none',
};

export default function UnitsTable({
  projectId, configurationId, config, project,
  units, loading,
  statusFilter, onStatusFilterChange,
  priceSort, onPriceSortChange,
  searchQuery, onSearchQueryChange,
  onImportCSV, onAddUnit, onUnitStatusChange,
  unitStatusOptions,
}) {
  const navigate = useNavigate();
  const [search, setSearch]           = useState(searchQuery ?? '');
  const [highlightPopover, setHighlightPopover] = useState(null); // { anchorEl, highlights: string[] }

  // Status options from API; fallback to constants
  const statusFilterChips = useMemo(() => {
    const list = unitStatusOptions?.length ? unitStatusOptions : UNIT_STATUS_FILTERS_FALLBACK.filter((o) => o.value !== 'all');
    return [{ value: 'all', label: 'All' }, ...list];
  }, [unitStatusOptions]);

  const statusCfg = useMemo(() => {
    const map = { ...DEFAULT_STATUS_CFG };
    (unitStatusOptions || UNIT_STATUS_FILTERS_FALLBACK).forEach((o) => {
      const v = (o.value || '').toLowerCase();
      if (map[v]) map[v] = { ...map[v], label: o.label || map[v].label };
    });
    return map;
  }, [unitStatusOptions]);

  // Statuses that can be set in the table (booked is only set when a booking is created)
  const settableStatusOptions = useMemo(() => {
    const list = (unitStatusOptions || UNIT_STATUS_FILTERS_FALLBACK).filter(
      (o) => o.value !== 'all' && SETTABLE_UNIT_STATUSES.includes((o.value || '').toLowerCase())
    );
    return list;
  }, [unitStatusOptions]);

  const filtered = useMemo(() => {
    let list = [...(units || [])];
    const status = statusFilter && statusFilter !== 'all' ? statusFilter : null;
    if (status) list = list.filter((u) => normalizeStatus(u.inventory_status) === status);
    const q = (search || searchQuery || '').toLowerCase().trim();
    if (q) list = list.filter((u) => {
      const name = (u.name || '').toLowerCase();
      const code = (u.unit_code || '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
    if (priceSort === 'asc')  list.sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));
    if (priceSort === 'desc') list.sort((a, b) => (Number(b.price) || 0) - (Number(a.price) || 0));
    return list;
  }, [units, statusFilter, search, searchQuery, priceSort]);

  const handleSearchChange = (e) => { setSearch(e.target.value); onSearchQueryChange?.(e.target.value); };

  const cfgCategory   = config?.category_display || config?.category || '';
  const cfgUnits      = units?.length ?? 0;
  const cfgCommission = config?.commission ?? config?.metadata?.commission_rate;
  const titleSub      = [cfgCategory, cfgUnits ? `${cfgUnits} units` : '', cfgCommission != null ? `${cfgCommission}% commission` : ''].filter(Boolean).join(' · ');

  return (
    // .inv-panel — flex: 1 so this column consumes all space; minHeight/minWidth 0 so table area fills and scrolls
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>

      {/* .inv-header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: '8px',
        px: '16px', py: '11px',
        background: UnitTableStyles.white, borderBottom: `1px solid ${UnitTableStyles.border}`,
        flexShrink: 0, flexWrap: 'wrap',
      }}>
        {/* .inv-title */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '13px', fontWeight: 700, color: UnitTableStyles.ink,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            display: 'inline',
          }}>
            {config?.name || 'Select a configuration'}
          </Typography>
          {titleSub && (
            <Typography component="span" sx={{ fontSize: '11.5px', color: UnitTableStyles.subtle, fontWeight: 400, ml: '4px' }}>
              · {titleSub}
            </Typography>
          )}
        </Box>

        {/* .view-toggle (Table | Matrix) */}
        <Box sx={{
          display: 'flex', background: UnitTableStyles.surface,
          border: `1.5px solid ${UnitTableStyles.border}`, borderRadius: '8px', overflow: 'hidden',
          flexShrink: 0,
        }}>
           {/* { id: 'matrix', label: '🏢 Matrix', active: false } */}
          {/* {[{ id: 'table', label: '⊞ Table', active: true },].map((vt) => (
            <Box key={vt.id} component="button" sx={{
              px: '10px', py: '4px', fontSize: '12px', fontWeight: 600,
              cursor: 'pointer', border: 'none', fontFamily: 'inherit',
              background: vt.active ? UnitTableStyles.white : 'transparent',
              color: vt.active ? UnitTableStyles.g800 : UnitTableStyles.subtle,
              boxShadow: vt.active ? '0 1px 2px rgba(15,31,21,.06)' : 'none',
              transition: 'all 150ms ease',
            }}>
              {vt.label}
            </Box>
          ))} */}
        </Box>

        {/* .btn-secondary — Import CSV */}
        {/* <Box component="button" onClick={onImportCSV} sx={{
          ...btnBase,
          px: '11px', py: '5px',
          background: UnitTableStyles.surface2, color: UnitTableStyles.ink2,
          border: `1.5px solid ${UnitTableStyles.border}`,
          '&:hover': { borderColor: UnitTableStyles.border2, background: UnitTableStyles.surface },
        }}>
          ⬆ Import CSV
        </Box> */}

        {/* .btn-accent — Add Unit */}
        <Box component="button" onClick={onAddUnit} disabled={!configurationId} sx={{
          ...btnBase,
          px: '11px', py: '5px',
          background: !configurationId ? UnitTableStyles.border2 : UnitTableStyles.mint,
          color: '#fff',
          boxShadow: configurationId ? '0 2px 8px rgba(0,196,109,.28)' : 'none',
          '&:hover': configurationId ? {
            filter: 'brightness(1.05)', transform: 'translateY(-1px)',
            boxShadow: '0 4px 14px rgba(0,196,109,.35)',
          } : {},
        }}>
          + Add Unit
        </Box>
      </Box>

      {/* .inv-toolbar */}
      <Box sx={{
        display: 'flex', gap: '8px', px: '14px', py: '9px',
        background: UnitTableStyles.white, borderBottom: `1px solid ${UnitTableStyles.border}`,
        flexShrink: 0, alignItems: 'center', flexWrap: 'wrap',
      }}>
        {/* Search — .srch */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: '7px',
          background: UnitTableStyles.surface, border: `1.5px solid ${UnitTableStyles.border}`,
          borderRadius: '8px', px: '11px', py: '7px',
          minWidth: '180px',
          transition: 'all 150ms',
          '&:focus-within': {
            borderColor: UnitTableStyles.mint, background: UnitTableStyles.white,
            boxShadow: `0 0 0 3px ${UnitTableStyles.mintSoft}`,
          },
        }}>
          <SearchIcon sx={{ fontSize: 12, color: UnitTableStyles.subtle, flexShrink: 0 }} />
          <Box
            component="input"
            placeholder="Search unit name or ID…"
            value={search}
            onChange={handleSearchChange}
            sx={{
              border: 'none', background: 'transparent',
              fontSize: '12px', color: UnitTableStyles.ink, outline: 'none',
              width: '100%', fontFamily: 'inherit',
              '&::placeholder': { color: UnitTableStyles.subtle },
            }}
          />
        </Box>

        {/* .chips — status filter pills */}
        <Box sx={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          {statusFilterChips.map(({ value, label }) => {
            const isOn = (statusFilter || 'all') === value;
            return (
              <Box
                key={value}
                component="button"
                onClick={() => onStatusFilterChange?.(value)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  fontSize: '11.5px', fontWeight: isOn ? 600 : 500,
                  px: '12px', py: '4px', borderRadius: '99px',
                  cursor: 'pointer', fontFamily: 'inherit',
                  border: `1.5px solid ${isOn ? UnitTableStyles.mint : UnitTableStyles.border}`,
                  color: isOn ? UnitTableStyles.g700 : UnitTableStyles.muted,
                  background: isOn ? UnitTableStyles.mintSoft : 'transparent',
                  transition: 'all 150ms ease',
                  '&:hover': { borderColor: UnitTableStyles.border2, color: UnitTableStyles.ink2 },
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>

        {/* Right: sort + export */}
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Box
            component="select"
            value={priceSort || ''}
            onChange={(e) => onPriceSortChange?.(e.target.value)}
            sx={{
              border: `1.5px solid ${UnitTableStyles.border}`, borderRadius: '8px',
              px: '9px', py: '5px', fontSize: '11px', color: UnitTableStyles.ink,
              outline: 'none', background: UnitTableStyles.white,
              cursor: 'pointer', fontFamily: 'inherit',
              '&:focus': { borderColor: UnitTableStyles.mint },
              transition: 'border-color 150ms',
            }}
          >
            <option value="">Sort: Default</option>
            <option value="asc">Price ↑</option>
            <option value="desc">Price ↓</option>

          </Box>
          {/* .btn-ghost — Export */}
          <Box component="button" sx={{
            ...btnBase,
            px: '9px', py: '6px', background: 'transparent',
            border: 'none', color: UnitTableStyles.subtle,
            '&:hover': { background: UnitTableStyles.surface2, color: UnitTableStyles.ink2 },
          }}>
            ⬇ Export
          </Box>
        </Box>
      </Box>

      {/* ── Table / empty states ── */}
      {loading ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
          <TableLoadingState message="Loading units…" theme={TABLE_THEME} size={28} />
        </Box>
      ) : !configurationId ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', pt: 3, px: 2 }}>
          <Box sx={{ textAlign: 'center', py: '48px' }}>
            <Typography sx={{ fontSize: '40px', mb: '10px' }}>📋</Typography>
            <Typography sx={{ fontFamily: "'Fraunces', serif", fontSize: '16px', fontWeight: 700, color: UnitTableStyles.ink, mb: '5px' }}>
              No configuration selected
            </Typography>
            <Typography sx={{ fontSize: '12px', color: UnitTableStyles.muted }}>
              Select a configuration from the sidebar to view units.
            </Typography>
          </Box>
        </Box>
      ) : (
        // .unit-table-wrap — SmartTable theme and row styles
        <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'auto', minHeight: 0, position: 'relative' }} className="smart-table-wrap">
          <style>{TABLE_ROW_GLOBAL_CSS}</style>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: '12.5px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr>
                <SmartTableTH theme={TABLE_THEME}>Unit Name</SmartTableTH>
                <SmartTableTH theme={TABLE_THEME}>Floor/Block/Tower</SmartTableTH>
                <SmartTableTH theme={TABLE_THEME}>Facing</SmartTableTH>
                <SmartTableTH theme={TABLE_THEME}>
                  <span>
                    Area
                    <br />
                    <span style={{ fontSize: '9px', fontStyle: 'italic', textTransform: 'none', letterSpacing: 0, color: TABLE_THEME.subtle, fontWeight: 500 }}>
                      carpet|built-up|super BUA|total
                    </span>
                  </span>
                </SmartTableTH>
                <SmartTableTH theme={TABLE_THEME}>Price</SmartTableTH>
                <SmartTableTH theme={TABLE_THEME}>Highlights</SmartTableTH>
                <SmartTableTH theme={TABLE_THEME} align="center">Commission</SmartTableTH>
                <SmartTableTH theme={TABLE_THEME}>Status</SmartTableTH>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <TableEmptyState
                  message="No units match"
                  description="Add units or adjust filters."
                  theme={TABLE_THEME}
                  colSpan={8}
                />
              ) : (
                filtered.map((unit, rowIdx) => {
                  const statusKey = normalizeStatus(unit.inventory_status);
                  const pill      = statusCfg[statusKey] || statusCfg.available;

                  // --- Area: use backend area_list (normalized strings), fallback to local normalize
                  const areaList = Array.isArray(unit.area_list) && unit.area_list.length > 0
                    ? unit.area_list
                    : normalizeAreaList(unit.area);

                  // --- Key highlights: list of strings; show first only, +N more opens tooltip ---
                  const highlights     = Array.isArray(unit.key_highlights)
                    ? unit.key_highlights.map((h) => (typeof h === 'string' ? h : String(h ?? ''))).filter(Boolean)
                    : [];
                  const firstHighlight = highlights[0] ?? null;
                  const extraCount     = highlights.length > 1 ? highlights.length - 1 : 0;

                  // --- Commission ---
                  const commDisplay   = getEffectiveCommissionDisplay(unit, config, project);

                  const handleRowClick = (e) => {
                    if (e.target.closest('button, select, input')) return;
                    navigate(`/inventory/projects/${projectId}/configurations/${configurationId}/units/${unit.id}`);
                  };

                  const cellStyle = { paddingLeft: 14, paddingRight: 14 };

                  return (
                    <tr
                      key={unit.id}
                      className={TABLE_ROW_CLASS}
                      style={{ cursor: 'pointer' }}
                      onClick={handleRowClick}
                    >
                      {/* Unit Name */}
                      <SmartTableTD theme={TABLE_THEME} style={cellStyle}>
                        <span style={{ fontSize: '12.5px', fontWeight: 600, color: TABLE_THEME.ink }}>
                          {unit.name || '—'}
                        </span>
                      </SmartTableTD>

                      {/* Floor/Block/Tower */}
                      <SmartTableTD theme={TABLE_THEME} style={cellStyle}>
                        <span style={{ fontSize: '12px', color: TABLE_THEME.ink2 }}>
                          {[unit.floor != null ? `Floor ${unit.floor}` : null, unit.unit_block ?? unit.metadata?.tower_wing].filter(Boolean).join(' · ') || '—'}
                        </span>
                      </SmartTableTD>

                      <SmartTableTD theme={TABLE_THEME} style={cellStyle}>
                        <span style={{ fontSize: '12px', color: TABLE_THEME.ink2 }}>
                          {(unit.facing ?? unit.metadata?.facing)
                            ? (unit.facing ?? unit.metadata.facing).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                            : '—'}
                        </span>
                      </SmartTableTD>
                      {/* Area */}
                      <SmartTableTD theme={TABLE_THEME} style={cellStyle}>
                        {(() => {
                          const display = pickPrimaryArea(unit, config?.category);
                          return display
                            ? <span style={{ fontSize: '12px', color: TABLE_THEME.ink2 }}>{display}</span>
                            : <span style={{ color: TABLE_THEME.subtle }}>—</span>;
                        })()}
                      </SmartTableTD>

                      {/* Price */}
                      <SmartTableTD theme={TABLE_THEME} style={cellStyle}>
                        <span style={{ fontWeight: 700, color: TABLE_THEME.ink2, fontSize: '13px' }}>
                          {fmtPrice(unit.price)}
                        </span>
                      </SmartTableTD>

                      {/* Key Highlights */}
                      <SmartTableTD theme={TABLE_THEME} style={cellStyle} onClick={(e) => e.stopPropagation()}>
                        {firstHighlight ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'nowrap' }}>
                            <span style={{
                              display: 'inline-block',
                              maxWidth: '120px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '11px',
                              color: UnitTableStyles.ink2,
                              background: UnitTableStyles.surface2,
                              border: `1px solid ${UnitTableStyles.border}`,
                              borderRadius: '5px',
                              padding: '2px 7px',
                            }}>
                              {firstHighlight}
                            </span>
                            {extraCount > 0 && (
                              <Box
                                component="button"
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setHighlightPopover({ anchorEl: e.currentTarget, highlights });
                                }}
                                sx={{
                                  fontSize: '10px', fontWeight: 700,
                                  color: UnitTableStyles.subtle,
                                  background: UnitTableStyles.surface,
                                  border: `1px solid ${UnitTableStyles.border}`,
                                  borderRadius: '5px',
                                  padding: '2px 5px',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                  fontFamily: 'inherit',
                                  '&:hover': { background: UnitTableStyles.surface2, color: UnitTableStyles.ink2, borderColor: UnitTableStyles.border2 },
                                }}
                              >
                                +{extraCount} more
                              </Box>
                            )}
                          </div>
                        ) : (
                          <span style={{ color: TABLE_THEME.subtle, fontSize: '12px' }}>—</span>
                        )}
                      </SmartTableTD>

                      {/* Commission */}
                      <SmartTableTD theme={TABLE_THEME} align="center" style={cellStyle}>
                        {commDisplay?.text ? (
                          <span style={{
                            fontSize: '11.5px', fontWeight: 600,
                            color: UnitTableStyles.g600,
                            background: UnitTableStyles.mintSoft,
                            border: `1px solid ${UnitTableStyles.g100}`,
                            borderRadius: '5px',
                            padding: '2px 7px',
                          }}>
                            {commDisplay.text}
                          </span>
                        ) : (
                          <span style={{ color: TABLE_THEME.subtle, fontSize: '12px' }}>—</span>
                        )}
                      </SmartTableTD>

                      {/* Status */}
                      <SmartTableTD theme={TABLE_THEME} style={cellStyle}>
                        {statusKey === 'booked' ? (
                          <span style={{
                            display: 'inline-flex', alignItems: 'center',
                            px: '10px', py: '3px', borderRadius: '99px',
                            fontSize: '11px', fontWeight: 700,
                            background: pill.bg, color: pill.color,
                            border: `1px solid ${pill.bg}`,
                          }}>
                            {pill.label}
                          </span>
                        ) : (
                          <Box
                            component="select"
                            value={statusKey}
                            onChange={(e) => onUnitStatusChange?.(unit, e.target.value)}
                            sx={{
                              display: 'inline-flex', alignItems: 'center',
                              px: '10px', py: '3px', borderRadius: '99px',
                              fontSize: '11px', fontWeight: 700,
                              cursor: 'pointer', fontFamily: 'inherit',
                              background: pill.bg, color: pill.color,
                              border: `1px solid ${pill.bg}`,
                              outline: 'none', appearance: 'auto',
                              transition: 'all 150ms ease',
                            }}
                          >
                            {settableStatusOptions.map((o) => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </Box>
                        )}
                      </SmartTableTD>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </Box>
      )}

      {/* Popover: all key highlights when "+N more" is clicked */}
      <Popover
        open={Boolean(highlightPopover?.anchorEl)}
        anchorEl={highlightPopover?.anchorEl}
        onClose={() => setHighlightPopover(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { mt: 0.75, borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 180, maxWidth: 280 } }}
      >
        <Box sx={{ p: 1.5 }}>
          <Typography sx={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.6px', color: UnitTableStyles.subtle, mb: 1 }}>Key highlights</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, pr: 0.5, py: 0 }}>
            {(highlightPopover?.highlights ?? []).map((h, i) => (
              <Typography key={i} component="li" sx={{ fontSize: '12px', color: UnitTableStyles.ink2, mb: 0.5, '&:last-child': { mb: 0 } }}>
                {h}
              </Typography>
            ))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
